import React, { useState, useEffect, useRef } from "react";
import { Form, Button, Spinner, Alert } from "react-bootstrap";
import CustomModal from "../../Global/Modal/CustomModal";
import { sendEmail } from "./EmailBackendFunctions";
import "./EmailSendingModal.css";
import { createEmailWrapper } from "./emailTemplates";

const EmailSendingModal = ({
    isOpen,
    onClose,
    title = "שליחת אימייל",
    subtitle,
    recipients = [],
    onSuccessSend,
    isLoading = false,
    error = null,
    success = null,
    allowRecipientSelection = true,
    defaultSubject = "",
    emailBody = "",
    event = null,
}) => {
    const [selectedRecipients, setSelectedRecipients] = useState([]);
    const [subject, setSubject] = useState(defaultSubject);
    const [body, setBody] = useState(emailBody);
    const [useCustomTemplate, setUseCustomTemplate] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [isSending, setIsSending] = useState(false);
    const [sendStatus, setSendStatus] = useState(null); // 'sending', 'success', 'error'
    const [sendError, setSendError] = useState(null);
    const iframeRef = useRef(null);
    const isSendingRef = useRef(false);

    // Reset form when modal opens/closes
    useEffect(() => {
        if (isOpen) {
            // Only reset if we're not currently in a sending state
            if (!isSendingRef.current) {
                setSelectedRecipients(recipients);
                setSubject(defaultSubject);
                setBody(emailBody);
                setUseCustomTemplate(false);
                setValidationErrors({});
            }
            setIsSending(false);
            setSendStatus(null);
            setSendError(null);
            isSendingRef.current = false;
        }
    }, [isOpen, recipients, defaultSubject, emailBody]);

    // Update iframe content when body changes or modal opens
    useEffect(() => {
        if (iframeRef.current && isOpen) {
            const iframe = iframeRef.current;
            const doc = iframe.contentDocument || iframe.contentWindow.document;
            doc.open();
            doc.write(`
                <!DOCTYPE html>
                <html dir="rtl" lang="he">
                <head>
                    <meta charset="UTF-8">
                    <meta name="viewport" content="width=device-width, initial-scale=1.0">
                    <link href="https://fonts.googleapis.com/css2?family=Assistant:wght@200;300;400;500;600;700;800&display=swap" rel="stylesheet">
                    <style>
                        body {
                            font-family: 'Assistant', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                            line-height: 1.6;
                            color: #333;
                            margin: 0;
                            padding: 0;
                            background-color: #f4f4f4;
                            direction: rtl;
                            text-align: right;
                        }
                        .email-container {
                            max-width: 600px;
                            margin: 0 auto;
                            background-color: #ffffff;
                            box-shadow: 0 0 10px rgba(0,0,0,0.1);
                            direction: rtl;
                            text-align: right;
                        }
                        .email-header {
                            background: linear-gradient(135deg, #39b54a 0%, #2d8e3a 100%);
                            color: white;
                            padding: 30px 20px;
                            text-align: center;
                            position: relative;
                        }
                        .email-header h1 {
                            margin: 0;
                            font-size: 32px;
                            font-weight: 700;
                            font-family: 'Assistant', sans-serif;
                        }
                        .email-body {
                            padding: 30px 20px;
                            font-family: 'Assistant', sans-serif;
                            direction: rtl;
                            text-align: right;
                        }
                        .event-details {
                            background: #f8f9fa;
                            border-radius: 8px;
                            padding: 20px;
                            margin: 20px 0;
                            border-right: 4px solid #39b54a;
                            font-family: 'Assistant', sans-serif;
                            direction: rtl;
                            text-align: right;
                        }
                        .event-details h3 {
                            margin-top: 0;
                            color: #333;
                            font-size: 18px;
                            font-family: 'Assistant', sans-serif;
                        }
                        .event-details p {
                            margin: 8px 0;
                            font-size: 14px;
                            font-family: 'Assistant', sans-serif;
                        }
                        .event-details strong {
                            color: #39b54a;
                            font-weight: 600;
                            font-family: 'Assistant', sans-serif;
                        }
                        .payment-button {
                            display: inline-block;
                            background: #39b54a;
                            color: white;
                            padding: 12px 24px;
                            text-decoration: none;
                            border-radius: 6px;
                            font-weight: 600;
                            margin: 10px 0;
                            transition: background-color 0.3s;
                            font-family: 'Assistant', sans-serif;
                        }
                        .payment-button:hover {
                            background: #2d8e3a;
                        }
                        .email-footer {
                            background: #f8f9fa;
                            padding: 20px;
                            text-align: center;
                            border-top: 1px solid #e9ecef;
                            font-family: 'Assistant', sans-serif;
                            direction: rtl;
                        }
                        .email-footer p {
                            margin: 0;
                            color: #666;
                            font-size: 14px;
                            font-family: 'Assistant', sans-serif;
                        }
                        @media only screen and (max-width: 600px) {
                            .email-container {
                                margin: 0;
                                box-shadow: none;
                            }
                            .email-header, .email-body, .email-footer {
                                padding: 20px 15px;
                            }
                        }
                    </style>
                </head>
                <body>
                    <div class="email-container">
                        ${body}
                    </div>
                </body>
                </html>
            `);
            doc.close();
        }
    }, [body, isOpen]);

    // Group recipients by student name
    const groupRecipientsByStudent = () => {
        const grouped = {};

        recipients.forEach(recipient => {
            // Use the recipient name directly as student name
            const studentName = recipient.name;

            if (!grouped[studentName]) {
                grouped[studentName] = {
                    studentName,
                    emails: []
                };
            }

            grouped[studentName].emails.push({
                id: recipient.id,
                email: recipient.email,
                originalName: recipient.name
            });
        });

        return Object.values(grouped);
    };

    // Handle student selection (toggle all emails for a student)
    const handleStudentToggle = (studentGroup) => {
        const studentIds = studentGroup.emails.map(email => email.id);
        const isFullySelected = studentIds.every(id => selectedRecipients.some(r => r.id === id));

        setSelectedRecipients(prev => {
            if (isFullySelected) {
                // Remove all emails for this student
                return prev.filter(r => !studentIds.includes(r.id));
            } else {
                // Add all emails for this student that aren't already selected
                const newRecipients = [...prev];
                studentGroup.emails.forEach(email => {
                    if (!newRecipients.some(r => r.id === email.id)) {
                        newRecipients.push({
                            id: email.id,
                            name: email.originalName,
                            email: email.email
                        });
                    }
                });
                return newRecipients;
            }
        });
    };

    // Check if all emails for a student are selected
    const isStudentFullySelected = (studentGroup) => {
        const studentIds = studentGroup.emails.map(email => email.id);
        return studentIds.every(id => selectedRecipients.some(r => r.id === id));
    };

    // Validate form
    const validateForm = () => {
        const errors = {};

        if (selectedRecipients.length === 0) {
            errors.recipients = "יש לבחור לפחות נמען אחד";
        }

        if (!subject.trim()) {
            errors.subject = "יש להזין נושא";
        }

        if (!body.trim()) {
            errors.body = "יש להזין תוכן ההודעה";
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Handle send
    const handleSend = async () => {
        if (!validateForm()) return;

        setIsSending(true);
        isSendingRef.current = true;
        setSendStatus('sending');
        setSendError(null);

        try {
            // If onSend callback is provided, use it (for custom handling)
            if (onSuccessSend) {
                onSuccessSend();
            }

            // Otherwise, use the default backend function
            const completeEmail = createEmailWrapper(body, subject.trim());
            const result = await sendEmail(
                selectedRecipients,
                subject.trim(),
                completeEmail
            );

            // Handle success
            console.log("Email sent successfully:", result);
            setSendStatus('success');

            // Show success message and close modals after delay
            setTimeout(() => {
                setIsSending(false);
                isSendingRef.current = false;
                onClose();
            }, 3000);

        } catch (error) {
            console.error("Failed to send email:", error);
            setSendStatus('error');
            setSendError(error.message || 'אירעה שגיאה בשליחת האימייל');

            // Reset sending state after error
            setTimeout(() => {
                setIsSending(false);
                isSendingRef.current = false;
            }, 3000);
        }
    };

    // Footer buttons
    const footerButtons = [
        {
            title: "ביטול",
            style: "grey",
            onClick: onClose,
            disabled: isLoading || isSending,
        },
        {
            title: "שלח אימייל",
            style: "primary",
            onClick: handleSend,
            disabled: isLoading || isSending || selectedRecipients.length === 0,
        },
    ];

    return (
        <>
            <CustomModal
                isOpen={isOpen}
                onClose={onClose}
                title={title}
                subtitle={subtitle}
                footerButtons={footerButtons}
                width={70}
                height={90}
            >
                <div className="email-sending-modal-content">
                    {/* Error/Success Messages */}
                    {error && (
                        <Alert variant="danger" className="mb-3">
                            {error}
                        </Alert>
                    )}
                    {success && (
                        <Alert variant="success" className="mb-3">
                            {success}
                        </Alert>
                    )}

                    {/* Subject */}
                    <div className="subject-section mb-4">
                        <h5 className="section-title">נושא</h5>
                        <Form.Control
                            type="text"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            placeholder="הזן נושא האימייל"
                            isInvalid={!!validationErrors.subject}
                            disabled={isSending}
                        />
                        {validationErrors.subject && (
                            <Form.Control.Feedback type="invalid">
                                {validationErrors.subject}
                            </Form.Control.Feedback>
                        )}
                    </div>

                    {/* Email Preview and Recipients Row */}
                    <div className="email-preview-recipients-row">
                        {/* Recipients Section */}
                        {allowRecipientSelection && recipients.length > 0 && (
                            <div className="recipients-section">
                                <h5 className="section-title">נמענים</h5>
                                <div className="recipients-controls mb-2">
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={() => {
                                            const allStudentIds = groupRecipientsByStudent().flatMap(group => group.emails.map(email => email.id));
                                            const isFullySelected = allStudentIds.every(id => selectedRecipients.some(r => r.id === id));
                                            setSelectedRecipients(prev => {
                                                if (isFullySelected) {
                                                    return prev.filter(r => !allStudentIds.includes(r.id));
                                                } else {
                                                    const newRecipients = [...prev];
                                                    groupRecipientsByStudent().forEach(group => {
                                                        group.emails.forEach(email => {
                                                            if (!newRecipients.some(r => r.id === email.id)) {
                                                                newRecipients.push({
                                                                    id: email.id,
                                                                    name: email.originalName,
                                                                    email: email.email
                                                                });
                                                            }
                                                        });
                                                    });
                                                    return newRecipients;
                                                }
                                            });
                                        }}
                                        className="me-2"
                                        disabled={isSending}
                                    >
                                        {groupRecipientsByStudent().every(group => isStudentFullySelected(group)) ? "בטל הכל" : "בחר הכל"}
                                    </Button>
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => setSelectedRecipients([])}
                                        disabled={isSending}
                                    >
                                        בטל הכל
                                    </Button>
                                </div>
                                <div className="recipients-list">
                                    {groupRecipientsByStudent().map((studentGroup) => (
                                        <div
                                            key={studentGroup.studentName}
                                            className={`student-recipient-group ${isStudentFullySelected(studentGroup) ? "fully-selected" : ""}`}
                                        >
                                            {/* Student Header */}
                                            <div
                                                className="student-header"
                                                onClick={() => !isSending && handleStudentToggle(studentGroup)}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={isStudentFullySelected(studentGroup)}
                                                    onChange={() => !isSending && handleStudentToggle(studentGroup)}
                                                    className="student-checkbox"
                                                    disabled={isSending}
                                                />
                                                <div className="student-name">{studentGroup.studentName}</div>
                                            </div>

                                            {/* Email List */}
                                            <div className="student-emails">
                                                {studentGroup.emails.map((email) => (
                                                    <div
                                                        key={email.id}
                                                        className="email-item"
                                                    >
                                                        <div className="email-address">{email.email}</div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {validationErrors.recipients && (
                                    <div className="validation-error">{validationErrors.recipients}</div>
                                )}
                                <div className="recipients-summary">
                                    נבחרו {selectedRecipients.length} מתוך {recipients.length} נמענים
                                </div>
                            </div>
                        )}

                        {/* Email Preview */}
                        <div className="email-preview-section">
                            <h5 className="section-title">תצוגה מקדימה</h5>
                            <div className="email-preview-container">
                                <iframe
                                    ref={iframeRef}
                                    title="Email Preview"
                                    className="email-preview-iframe"
                                    sandbox="allow-same-origin"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </CustomModal>

            {/* Loading/Success Modal */}
            <CustomModal
                isOpen={isSending}
                onClose={() => { }} // Prevent closing during sending
                title={sendStatus === 'success' ? 'המיילים נשלחו בהצלחה!' : 'שולח מיילים'}
                width={40}
                height={40}
                footerButtons={[]}
            >
                <div className="email-sending-status-content">
                    {sendStatus === 'success' ? (
                        <div className="success-content">
                            <div className="success-icon">✓</div>
                            <p className="success-message">המיילים נשלחו בהצלחה!</p>
                        </div>
                    ) : sendStatus === 'error' ? (
                        <div className="error-content">
                            <div className="error-icon">✗</div>
                            <p className="error-message">{sendError}</p>
                        </div>
                    ) : (
                        <div className="loading-content">
                            <div className="loading-spinner">
                                <Spinner animation="border" role="status" />
                            </div>
                            <p className="loading-message">שולח מיילים...</p>
                        </div>
                    )}
                </div>
            </CustomModal>
        </>
    );
};

export default EmailSendingModal;
